/**
 * TD variantes — Depósito Todo Dental
 * 1) <td-pedido-rapido>: lista de pedido rápido en la página de producto (un renglón por variante).
 * 2) <td-cuadricula>: cuadrícula de pedido para productos con 2 opciones (p. ej. coronas Zona × Número).
 * 3) <td-agregar-rapido>: chips + cantidad + "Agregar" en las tarjetas de producto.
 * 4) Ventana de pedido rápido (desde las tarjetas: "+N", "Elegir tonos", "Elegir medidas").
 * 5) <td-factura>: datos de facturación en el carrito (atributos del carrito) y bloqueo del botón de pagar.
 * Agrega al carrito con /cart/add.js y avisa al tema Warehouse con el evento 'product:added'
 * (el tema refresca el contador y abre el cajón del carrito). Sin dependencias externas.
 */
(function () {
  'use strict';

  /* ---------- utilidades ---------- */

  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '$ {{amount}}';
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;

    function withDelimiters(number, precision, thousands, decimal) {
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      var fixed = (number / 100).toFixed(precision);
      var parts = fixed.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }

    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var value = '';
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      default: value = withDelimiters(cents, 2);
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function piezas(n) {
    return n === 1 ? 'pieza' : 'piezas';
  }

  function showMessage(el, text, type) {
    if (!el) return;
    el.className = el.className.replace(/\s*alert(--\w+)?/g, '');
    if (!text) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.className += ' alert ' + (type === 'success' ? 'alert--success' : 'alert--error');
    el.textContent = text;
  }

  /** Lee/limita el valor de un campo de cantidad. Devuelve {value, limited}. */
  function clampInput(input, min) {
    var max = input.hasAttribute('max') ? parseInt(input.getAttribute('max'), 10) : Infinity;
    var value = parseInt(input.value, 10);
    var limited = false;
    if (isNaN(value) || value < min) value = min;
    if (value > max) {
      value = Math.max(max, min);
      limited = true;
    }
    input.value = value;
    return { value: value, limited: limited, max: max };
  }

  /** Agrega varios renglones al carrito en una sola llamada y avisa al tema. */
  function addToCart(items, button) {
    var url = ((window.routes && window.routes.cartAddUrl) || '/cart/add') + '.js';
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (content) {
        if (!response.ok) {
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
          // Por si algo sí entró al carrito, refrescamos el contador del tema.
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { scrollToTop: false } }));
          var error = new Error(content.description || content.message || 'No se pudo agregar al carrito. Intenta de nuevo.');
          error.status = response.status;
          throw error;
        }

        var quantity = items.reduce(function (sum, item) { return sum + item.quantity; }, 0);
        var theme = window.theme || {};

        if (theme.pageType === 'cart' || theme.cartType === 'page') {
          window.location.href = (window.routes && window.routes.cartUrl) || '/cart';
          return content;
        }

        if (theme.cartType === 'drawer') {
          document.dispatchEvent(new CustomEvent('modal:close'));
        }

        // Evento estándar de Warehouse: actualiza el contador y abre el cajón del carrito.
        (button || document.documentElement).dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: { button: button, variant: null, quantity: quantity, productAdded: true }
        }));
        return content;
      });
    }, function () {
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      throw new Error('No hay conexión. Revisa tu internet e intenta de nuevo.');
    });
  }

  /** Precarga la foto de un chip (una sola vez por URL). */
  var preloaded = {};
  function preloadImage(chip, sizes) {
    if (!chip || !chip.hasAttribute('data-img')) return null;
    var src = chip.getAttribute('data-img');
    if (preloaded[src]) return preloaded[src];
    var image = new Image();
    image.decoding = 'async';
    image.sizes = sizes || '(max-width: 699px) 100vw, 600px';
    var srcset = chip.getAttribute('data-img-srcset');
    if (srcset) image.srcset = srcset;
    image.src = src;
    preloaded[src] = image;
    return image;
  }

  function blockEnter(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.target.blur();
    }
  }

  /* Pies con "Agregar todo al carrito" a la vista: html.td-pie-visible (lo usa el CSS para esconder WhatsApp en celular). */
  var piesVisibles = new Set();
  var marcarPies = function () {
    document.documentElement.classList.toggle('td-pie-visible', piesVisibles.size > 0);
  };
  var pieObserver = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) piesVisibles.add(entry.target); else piesVisibles.delete(entry.target);
    });
    marcarPies();
  }) : null;

  /* ---------- 1) Lista de pedido rápido (página de producto) ---------- */

  class PedidoRapido extends HTMLElement {
    connectedCallback() {
      if (this._td) return;
      this._td = true;
      this.rows = Array.prototype.slice.call(this.querySelectorAll('[data-td-row]'));
      this.addButton = this.querySelector('[data-td-add-all]');
      this.message = this.querySelector('[data-td-message]');

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('input', this.onInput.bind(this));
      this.addEventListener('keydown', function (event) {
        if (event.target.matches('[data-td-qty]')) blockEnter(event);
      });

      // Evita que el formulario del tema se envíe solo (p. ej. con Enter).
      var form = this.closest('form');
      if (form && !form._tdBlocked) {
        form._tdBlocked = true;
        form.addEventListener('submit', function (event) {
          if (form.querySelector('td-pedido-rapido, td-cuadricula')) event.preventDefault();
        });
      }

      // En celular, el botón flotante de WhatsApp (app Carthike) se esconde mientras el pie con
      // "Agregar todo al carrito" está a la vista, para que no lo tape (ver td-variantes.css, sección 6).
      this._pie = this.querySelector('.td-pr__footer');
      if (this._pie && pieObserver) pieObserver.observe(this._pie);

      this.update();
    }

    disconnectedCallback() {
      if (this._pie && pieObserver) {
        pieObserver.unobserve(this._pie);
        piesVisibles.delete(this._pie);
        marcarPies();
      }
    }

    onClick(event) {
      var step = event.target.closest('[data-td-step]');
      if (step && this.contains(step)) {
        var row = step.closest('[data-td-row]');
        var input = row.querySelector('[data-td-qty]');
        if (input.disabled) return;
        input.value = (parseInt(input.value, 10) || 0) + parseInt(step.getAttribute('data-td-step'), 10);
        this.checkRow(row);
        this.update();
        return;
      }
      if (event.target.closest('[data-td-add-all]')) {
        this.addAll();
      }
    }

    onChange(event) {
      var row = event.target.closest('[data-td-row]');
      if (row) {
        this.checkRow(row);
        this.update();
      }
    }

    onInput(event) {
      if (event.target.matches('[data-td-qty]')) this.update();
    }

    checkRow(row) {
      var input = row.querySelector('[data-td-qty]');
      var result = clampInput(input, 0);
      if (result.limited) {
        showMessage(this.message, 'Ya no hay más piezas disponibles de ' + row.getAttribute('data-label') + '. Si necesitas más, escríbenos por WhatsApp al 81 1596 1304.', 'error');
      } else {
        showMessage(this.message, '');
      }
    }

    update() {
      var totalQty = 0;
      var totalCents = 0;

      this.rows.forEach(function (row) {
        var input = row.querySelector('[data-td-qty]');
        var qty = Math.max(parseInt(input.value, 10) || 0, 0);
        var price = parseInt(row.getAttribute('data-price'), 10) || 0;
        totalQty += qty;
        totalCents += qty * price;
        row.classList.toggle('has-qty', qty > 0);
        var minus = row.querySelector('[data-td-step="-1"]');
        if (minus) minus.disabled = input.disabled || qty <= 0;
        var plus = row.querySelector('[data-td-step="1"]');
        if (plus && !input.disabled) {
          var max = input.hasAttribute('max') ? parseInt(input.getAttribute('max'), 10) : Infinity;
          plus.disabled = qty >= max;
        }
      });

      this.querySelector('[data-td-total-qty]').textContent = totalQty;
      this.querySelector('[data-td-total-word]').textContent = piezas(totalQty);
      this.querySelector('[data-td-total-money]').textContent = formatMoney(totalCents);

      if (this.addButton && !this.busy) {
        this.addButton.disabled = totalQty === 0;
        this.addButton.classList.toggle('button--disabled', totalQty === 0);
      }
    }

    addAll() {
      var self = this;
      if (this.busy) return;
      var items = [];

      this.rows.forEach(function (row) {
        var input = row.querySelector('[data-td-qty]');
        var qty = parseInt(input.value, 10) || 0;
        if (!input.disabled && qty > 0) {
          items.push({ id: parseInt(row.getAttribute('data-variant-id'), 10), quantity: qty });
        }
      });

      if (!items.length) {
        showMessage(this.message, 'Escribe la cantidad de al menos un renglón.', 'error');
        return;
      }

      var button = this.addButton;
      var originalText = button.textContent;
      this.busy = true;
      button.disabled = true;
      button.textContent = 'Agregando…';
      showMessage(this.message, '');

      addToCart(items, button).then(function () {
        var total = items.reduce(function (sum, item) { return sum + item.quantity; }, 0);
        self.rows.forEach(function (row) { row.querySelector('[data-td-qty]').value = self.emptyValue; });
        showMessage(self.message, 'Listo: se agregaron ' + total + ' ' + piezas(total) + ' al carrito.', 'success');
        // Aviso para la ventana de pedido rápido (la cierra). En la ficha del producto no hace nada.
        self.dispatchEvent(new CustomEvent('td:added', { bubbles: true, detail: { quantity: total } }));
      }).catch(function (error) {
        showMessage(self.message, self.errorText(error), 'error');
      }).then(function () {
        self.busy = false;
        button.textContent = originalText;
        self.update();
      });
    }

    /** Valor que se deja en los campos al limpiar (la lista usa 0; la cuadrícula, vacío). */
    get emptyValue() {
      return 0;
    }

    errorText(error) {
      return error.message;
    }
  }

  /* ---------- 2) Cuadrícula de pedido (productos con 2 o 3 opciones; con 3, pestañas) ---------- */
  /* Misma lógica que la lista: cada celda con variante es un [data-td-row] con su [data-td-qty].
     Sin tope en el HTML: si Shopify no tiene la cantidad, puede agregar solo lo disponible (igual que la lista);
     se refresca el contador del carrito y se muestra el aviso de WhatsApp sin números. */

  class Cuadricula extends PedidoRapido {
    connectedCallback() {
      if (this._td) return;
      var self = this;
      // Pestañas (productos con 3 opciones): todas las casillas de todas las pestañas suman al mismo pie.
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-td-tab]'));
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-td-panel]'));
      // Vista de lista: sus campos solo reflejan las casillas de la cuadrícula (la fuente de las cantidades).
      this.lrows = Array.prototype.slice.call(this.querySelectorAll('[data-td-lrow]'));
      this.gridByVariant = {};
      Array.prototype.forEach.call(this.querySelectorAll('[data-td-row]'), function (cell) {
        self.gridByVariant[cell.getAttribute('data-variant-id')] = cell;
      });
      this.viewButtons = Array.prototype.slice.call(this.querySelectorAll('[data-td-view]'));
      super.connectedCallback();

      this.setView(this.savedView(), false);
      this.addEventListener('click', function (event) {
        var button = event.target.closest('[data-td-view]');
        if (button && self.contains(button)) self.setView(button.getAttribute('data-td-view'), true);
      });
      this.addEventListener('input', function (event) {
        if (event.target.matches('[data-td-lqty]')) self.fromList(event.target, false);
      });
      this.addEventListener('change', function (event) {
        if (event.target.matches('[data-td-lqty]')) self.fromList(event.target, true);
      });
      this.addEventListener('keydown', function (event) {
        if (event.target.matches('[data-td-lqty]')) blockEnter(event);
      });

      if (!this.tabs.length) return;
      this.addEventListener('click', function (event) {
        var tab = event.target.closest('[data-td-tab]');
        if (tab && self.contains(tab)) self.selectTab(tab, false);
      });
      this.querySelector('[role="tablist"]').addEventListener('keydown', function (event) {
        var i = self.tabs.indexOf(document.activeElement);
        if (i < 0) return;
        var next = null;
        if (event.key === 'ArrowRight') next = self.tabs[(i + 1) % self.tabs.length];
        if (event.key === 'ArrowLeft') next = self.tabs[(i - 1 + self.tabs.length) % self.tabs.length];
        if (event.key === 'Home') next = self.tabs[0];
        if (event.key === 'End') next = self.tabs[self.tabs.length - 1];
        if (next) {
          event.preventDefault();
          self.selectTab(next, true);
        }
      });
    }

    /** Vista guardada por el cliente ('grid' | 'list') o null si nunca cambió (entonces se muestra la lista). */
    savedView() {
      try {
        var view = window.localStorage.getItem('td-vista-medidas');
        return view === 'grid' || view === 'list' ? view : null;
      } catch (e) {
        return null;
      }
    }

    setView(view, remember) {
      if (view) this.setAttribute('data-view', view);
      var current = view || this.getAttribute('data-view') || 'list'; // por omisión: lista en todas las pantallas
      this.viewButtons.forEach(function (button) {
        button.setAttribute('aria-pressed', button.getAttribute('data-td-view') === current ? 'true' : 'false');
      });
      if (remember && view) {
        try { window.localStorage.setItem('td-vista-medidas', view); } catch (e) { /* sin almacenamiento: solo esta vez */ }
      }
    }

    /** Un campo de la lista cambió: se copia a su casilla de la cuadrícula y se recalcula el pie. */
    fromList(input, final) {
      var row = input.closest('[data-td-lrow]');
      var cell = row && this.gridByVariant[row.getAttribute('data-variant-id')];
      if (!cell) return;
      var value = parseInt(input.value, 10);
      if (isNaN(value) || value < 0) value = 0;
      if (final) input.value = value;
      cell.querySelector('[data-td-qty]').value = value > 0 ? value : '';
      showMessage(this.message, '');
      this.update();
    }

    onClick(event) {
      var step = event.target.closest('[data-td-step]');
      var lrow = step && this.contains(step) && step.closest('[data-td-lrow]');
      if (lrow) {
        var input = lrow.querySelector('[data-td-lqty]');
        if (input.disabled) return;
        input.value = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.getAttribute('data-td-step'), 10));
        this.fromList(input, true);
        return;
      }
      super.onClick(event);
    }

    selectTab(tab, focus) {
      var key = tab.getAttribute('data-td-tab');
      this.tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      this.panels.forEach(function (p) {
        p.hidden = p.getAttribute('data-td-panel') !== key;
      });
      if (focus) tab.focus();
      if (tab.scrollIntoView) tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    update() {
      super.update();
      var self = this;
      (this.lrows || []).forEach(function (row) {
        var cell = self.gridByVariant[row.getAttribute('data-variant-id')];
        var gridInput = cell && cell.querySelector('[data-td-qty]');
        var qty = gridInput ? Math.max(parseInt(gridInput.value, 10) || 0, 0) : 0;
        var input = row.querySelector('[data-td-lqty]');
        // Mientras el cliente escribe en ese campo no se le cambia (p. ej. si lo dejó vacío para teclear otro número).
        if (document.activeElement !== input || (parseInt(input.value, 10) || 0) !== qty) input.value = qty;
        row.classList.toggle('has-qty', qty > 0);
        var minus = row.querySelector('[data-td-step="-1"]');
        if (minus) minus.disabled = input.disabled || qty <= 0;
      });
      if (!this.tabs || !this.tabs.length) return;
      this.tabs.forEach(function (tab) {
        var panel = self.querySelector('[data-td-panel="' + tab.getAttribute('data-td-tab') + '"]');
        var qty = 0;
        if (panel) {
          panel.querySelectorAll('[data-td-qty]').forEach(function (input) {
            if (!input.disabled) qty += Math.max(parseInt(input.value, 10) || 0, 0);
          });
        }
        var badge = tab.querySelector('[data-td-tab-count]');
        if (badge) {
          badge.textContent = qty;
          badge.hidden = qty === 0;
        }
        tab.classList.toggle('has-qty', qty > 0);
      });
    }

    get emptyValue() {
      return '';
    }

    checkRow(row) {
      var input = row.querySelector('[data-td-qty]');
      var value = parseInt(input.value, 10);
      input.value = isNaN(value) || value <= 0 ? '' : value;
      showMessage(this.message, '');
    }

    errorText(error) {
      if (error.status === 422) {
        return 'No tenemos toda esa cantidad disponible en línea. Revisa tu carrito: pudo agregarse solo una parte. Si necesitas más, escríbenos por WhatsApp al 81 1596 1304 y te ayudamos.';
      }
      return error.message;
    }
  }

  /* ---------- 3) Agregar rápido (tarjetas de producto) ---------- */

  class AgregarRapido extends HTMLElement {
    connectedCallback() {
      if (this._td) return;
      this._td = true;
      this.input = this.querySelector('[data-td-qty]');
      this.addButton = this.querySelector('[data-td-add]');
      this.message = this.querySelector('[data-td-message]');

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('keydown', function (event) {
        if (event.target.matches('[data-td-qty]')) blockEnter(event);
      });
      // Precarga la foto del tono al pasar el mouse / tocar el chip
      var preload = function (event) {
        var chip = event.target.closest && event.target.closest('[data-td-chip]');
        if (chip) preloadImage(chip);
      };
      this.addEventListener('pointerover', preload);
      this.addEventListener('focusin', preload);

      // Estado inicial (variante por omisión) para poder regresar a él
      var initialChip = this.querySelector('[data-td-chip].is-selected');
      this.initial = {
        chip: initialChip,
        id: this.getAttribute('data-variant-id'),
        available: this.getAttribute('data-available'),
        max: this.input.getAttribute('max')
      };
      this.userChose = false;
      this.refresh();
    }

    get available() {
      return this.getAttribute('data-available') === 'true';
    }

    onClick(event) {
      var chip = event.target.closest('[data-td-chip]');
      if (chip && this.contains(chip)) {
        event.preventDefault();
        if (this.userChose && chip.classList.contains('is-selected')) {
          this.resetChoice(); // tocar otra vez el tono elegido quita la selección
        } else {
          this.selectChip(chip);
          this.userChose = true;
          this.showPhoto(chip);
        }
        return;
      }

      var step = event.target.closest('[data-td-step]');
      if (step && this.contains(step)) {
        event.preventDefault();
        if (this.input.disabled) return;
        this.input.value = (parseInt(this.input.value, 10) || 1) + parseInt(step.getAttribute('data-td-step'), 10);
        this.check();
        this.refresh();
        return;
      }

      var add = event.target.closest('[data-td-add]');
      if (add && this.contains(add)) {
        event.preventDefault();
        this.add();
      }
    }

    onChange(event) {
      if (event.target === this.input) {
        this.check();
        this.refresh();
      }
    }

    selectChip(chip) {
      this.querySelectorAll('[data-td-chip]').forEach(function (other) {
        var selected = other === chip;
        other.classList.toggle('is-selected', selected);
        other.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      this.setAttribute('data-variant-id', chip.getAttribute('data-id'));
      this.setAttribute('data-available', chip.getAttribute('data-available'));
      if (chip.hasAttribute('data-max')) {
        var max = Math.max(parseInt(chip.getAttribute('data-max'), 10) || 0, 1);
        this.input.setAttribute('max', max);
      } else {
        this.input.removeAttribute('max');
      }
      showMessage(this.message, '');
      this.check(true);
      this.refresh();
    }

    /** Regresa la tarjeta a como cargó: variante por omisión y foto original del producto. */
    resetChoice() {
      var init = this.initial;
      this.userChose = false;
      if (init.chip) {
        this.selectChip(init.chip);
      }
      this.setAttribute('data-variant-id', init.id);
      this.setAttribute('data-available', init.available);
      if (init.max !== null) this.input.setAttribute('max', init.max); else this.input.removeAttribute('max');
      this.check(true);
      this.refresh();
      this.showPhoto(null);
    }

    /** Cambia la foto principal de la tarjeta por la del tono (o regresa la original con chip = null). */
    showPhoto(chip) {
      var item = this.closest('.product-item');
      var img = item && item.querySelector('.product-item__primary-image');
      if (!img) return;
      if (!this.originalPhoto) {
        this.originalPhoto = {
          src: img.getAttribute('src'),
          srcset: img.getAttribute('srcset'),
          mediaId: img.getAttribute('data-media-id')
        };
      }
      var target = chip && chip.hasAttribute('data-img')
        ? { src: chip.getAttribute('data-img'), srcset: chip.getAttribute('data-img-srcset'), mediaId: chip.getAttribute('data-media-id') }
        : this.originalPhoto;
      var isOriginal = target === this.originalPhoto || target.mediaId === this.originalPhoto.mediaId;
      item.classList.toggle('td-foto-variante', !!chip && !isOriginal);
      if (img.getAttribute('data-media-id') === target.mediaId && img.getAttribute('src') === target.src) return;

      var token = (this.photoToken = (this.photoToken || 0) + 1);
      var self = this;
      var apply = function () {
        if (token !== self.photoToken) return;
        img.removeAttribute('loading');
        if (target.srcset) img.setAttribute('srcset', target.srcset); else img.removeAttribute('srcset');
        img.setAttribute('src', target.src);
        if (target.mediaId) img.setAttribute('data-media-id', target.mediaId);
      };
      var pre = preloadImage(chip, img.getAttribute('sizes'));
      if (pre && !pre.complete) {
        pre.addEventListener('load', apply, { once: true });
        pre.addEventListener('error', apply, { once: true });
        setTimeout(apply, 1500);
      } else {
        apply();
      }
    }

    check(silent) {
      var result = clampInput(this.input, 1);
      if (result.limited && !silent) {
        showMessage(this.message, 'Ya no hay más piezas disponibles. Si necesitas más, escríbenos por WhatsApp al 81 1596 1304.', 'error');
      }
    }

    refresh() {
      var available = this.available;
      var qty = parseInt(this.input.value, 10) || 1;
      var max = this.input.hasAttribute('max') ? parseInt(this.input.getAttribute('max'), 10) : Infinity;

      this.input.disabled = !available;
      this.querySelector('[data-td-step="-1"]').disabled = !available || qty <= 1;
      this.querySelector('[data-td-step="1"]').disabled = !available || qty >= max;

      if (!this.busy) {
        this.addButton.disabled = !available;
        this.addButton.classList.toggle('button--primary', available);
        this.addButton.classList.toggle('button--disabled', !available);
        this.addButton.textContent = available ? 'Agregar' : 'Agotado';
      }
    }

    add() {
      var self = this;
      if (this.busy || !this.available) return;
      var qty = clampInput(this.input, 1).value;
      var id = parseInt(this.getAttribute('data-variant-id'), 10);
      var button = this.addButton;

      this.busy = true;
      button.disabled = true;
      button.textContent = 'Agregando…';
      showMessage(this.message, '');

      addToCart([{ id: id, quantity: qty }], button).then(function () {
        button.textContent = '✓ Agregado';
        setTimeout(function () {
          self.busy = false;
          self.input.value = 1;
          self.refresh();
        }, 1500);
      }).catch(function (error) {
        showMessage(self.message, error.message, 'error');
        self.busy = false;
        self.refresh();
      });
    }
  }


  /* ---------- 4) Ventana de pedido rápido (desde las tarjetas) ---------- */
  /* Se abre con cualquier elemento [data-td-open] (chip "+N", botón "Elegir tonos" o "Elegir medidas").
     El contenido es la sección td-ventana-pedido, pedida con la Section Rendering API, así que la tabla
     es exactamente la misma de la ficha del producto (mismos datos de existencia, claves y precios). */

  var CLOSE_ICON = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" fill="none"/></svg>';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var ventana = {
    dialog: null,
    opener: null,
    request: 0,

    build: function () {
      if (this.dialog) return this.dialog;
      var self = this;
      var dialog = document.createElement('dialog');
      dialog.className = 'td-modal';
      dialog.setAttribute('aria-labelledby', 'td-modal-title');
      dialog.innerHTML =
        '<header class="td-modal__header">' +
          '<div class="td-modal__heading">' +
            '<span class="td-modal__eyebrow">Lista de pedido rápido</span>' +
            '<h2 class="td-modal__title" id="td-modal-title"></h2>' +
          '</div>' +
          '<button type="button" class="td-modal__close" data-td-close aria-label="Cerrar">' + CLOSE_ICON + '</button>' +
        '</header>' +
        '<div class="td-modal__body" data-td-modal-body></div>';
      document.body.appendChild(dialog);

      // Cerrar tocando fuera (en el fondo oscuro): solo si el toque empezó y terminó fuera de la ventana.
      var downOnBackdrop = false;
      dialog.addEventListener('pointerdown', function (event) { downOnBackdrop = event.target === dialog; });
      dialog.addEventListener('click', function (event) {
        if (event.target.closest('[data-td-close]')) { self.close(); return; }
        if (event.target === dialog && downOnBackdrop) self.close();
        downOnBackdrop = false;
      });
      // Esc
      dialog.addEventListener('cancel', function (event) { event.preventDefault(); self.close(); });
      // Al agregar todo al carrito: el tema ya abrió el cajón; cerramos la ventana.
      dialog.addEventListener('td:added', function () { self.close(); });

      this.dialog = dialog;
      return dialog;
    },

    sectionUrl: function (url) {
      var u = new URL(url, window.location.origin);
      u.searchParams.set('section_id', 'td-ventana-pedido');
      var theme = window.Shopify && window.Shopify.theme;
      if (theme && theme.role && theme.role !== 'main' && theme.id) u.searchParams.set('preview_theme_id', theme.id);
      return u.pathname + u.search;
    },

    lockScroll: function (lock) {
      var root = document.documentElement;
      if (lock) {
        root.style.setProperty('--td-scrollbar', (window.innerWidth - root.clientWidth) + 'px');
        root.classList.add('td-scroll-lock');
      } else {
        root.classList.remove('td-scroll-lock');
        root.style.removeProperty('--td-scrollbar');
      }
    },

    open: function (opener) {
      var self = this;
      var url = opener.getAttribute('data-url') || opener.getAttribute('href');
      if (!url) return;
      var dialog = this.build();
      if (typeof dialog.showModal !== 'function') { window.location.href = url; return; }

      this.opener = opener;
      var body = dialog.querySelector('[data-td-modal-body]');
      dialog.querySelector('#td-modal-title').textContent = opener.getAttribute('data-title') || '';
      var eyebrow = dialog.querySelector('.td-modal__eyebrow');
      eyebrow.textContent = opener.closest('.td-ar--grid') ? 'Pedido por medidas' : 'Lista de pedido rápido';
      body.innerHTML = '<p class="td-modal__status" role="status">Cargando opciones…</p>';
      body.scrollTop = 0;

      dialog.classList.remove('is-closing');
      if (!dialog.open) dialog.showModal();
      this.lockScroll(true);
      dialog.querySelector('.td-modal__close').focus();

      var request = ++this.request;
      fetch(this.sectionUrl(url), { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then(function (html) {
          if (request !== self.request || !dialog.open) return;
          if (html.indexOf('td-pedido-rapido') === -1 && html.indexOf('td-cuadricula') === -1) throw new Error('sin tabla');
          body.innerHTML = html;
          body.scrollTop = 0;
          eyebrow.textContent = html.indexOf('<td-cuadricula') !== -1 ? 'Pedido por medidas' : 'Lista de pedido rápido';
        })
        .catch(function () {
          if (request !== self.request || !dialog.open) return;
          body.innerHTML = '<p class="td-modal__status">No se pudo cargar la lista. <a class="link link--accented" href="' + url + '">Abrir la ficha del producto</a></p>';
        });
    },

    close: function () {
      var self = this;
      var dialog = this.dialog;
      if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
      this.request++;

      var finish = function () {
        dialog.classList.remove('is-closing');
        if (dialog.open) dialog.close();
        dialog.querySelector('[data-td-modal-body]').innerHTML = '';
        self.lockScroll(false);
        if (self.opener && document.contains(self.opener)) {
          try { self.opener.focus({ preventScroll: true }); } catch (e) { self.opener.focus(); }
        }
        self.opener = null;
      };

      if (reduceMotion) { finish(); return; }
      dialog.classList.add('is-closing');
      setTimeout(finish, 200);
    }
  };

  document.addEventListener('click', function (event) {
    var opener = event.target.closest && event.target.closest('[data-td-open]');
    if (!opener) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return; // abrir en otra pestaña sigue funcionando
    event.preventDefault();
    ventana.open(opener);
  });

  window.TDVentanaPedido = ventana;

  /* ---------- 5) ¿Necesitas factura? (cajón y página del carrito) ---------- */
  /* Guarda los datos como atributos del carrito con /cart/update.js y los copia a <input type="hidden"
     name="attributes[...]"> dentro del formulario del carrito, para que también viajen al dar clic en pagar.
     Mientras "Necesito factura" esté marcada y falte algo obligatorio, el botón de pagar queda deshabilitado. */

  var FAC_KEYS = ['Factura', 'RFC', 'Razón social', 'Régimen fiscal', 'Uso CFDI', 'CP fiscal', 'Correo factura'];
  var USO_GENERAL = ['601', '603', '606', '612', '620', '621', '622', '623', '624', '625', '626']; // c_UsoCFDI G01/G03/I0x
  var RFC_CHARS = '0123456789ABCDEFGHIJKLMN&OPQRSTUVWXYZ Ñ';
  var facMemoria = null; // último estado capturado (sobrevive a que el tema vuelva a pintar el cajón)

  function rfcLimpio(value) {
    return (value || '').toUpperCase().replace(/[\s\-_.]/g, '');
  }

  /** Dígito verificador oficial del SAT (módulo 11) para un RFC de 12 o 13 caracteres. */
  function rfcDigito(rfc) {
    var base = rfc.length === 12 ? ' ' + rfc : rfc;
    var suma = 0;
    for (var i = 0; i < 12; i++) {
      var valor = RFC_CHARS.indexOf(base.charAt(i));
      if (valor < 0) return null;
      suma += valor * (13 - i);
    }
    var residuo = suma % 11;
    if (residuo === 0) return '0';
    var digito = 11 - residuo;
    return digito === 10 ? 'A' : String(digito);
  }

  function fechaValida(aammdd) {
    var aa = parseInt(aammdd.substr(0, 2), 10);
    var mm = parseInt(aammdd.substr(2, 2), 10);
    var dd = parseInt(aammdd.substr(4, 2), 10);
    if (mm < 1 || mm > 12 || dd < 1) return false;
    var dias = [31, aa % 4 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][mm - 1];
    return dd <= dias;
  }

  /** Revisa el RFC. Devuelve {error, aviso, tipo}: tipo 'm' (moral, 12) o 'f' (física, 13). */
  function revisarRfc(rfc) {
    if (!rfc) return { error: 'Escribe tu RFC.' };
    if (rfc.length === 18 && /^[A-Z]{4}\d{6}[HMX][A-Z]{5}[A-Z0-9]\d$/.test(rfc)) {
      return { error: 'Parece una CURP; escribe tu RFC (12 o 13 caracteres).' };
    }
    if (rfc.length !== 12 && rfc.length !== 13) {
      return { error: 'El RFC tiene ' + rfc.length + ' caracteres; debe tener 12 (persona moral) o 13 (persona física).' };
    }
    var tipo = rfc.length === 12 ? 'm' : 'f';
    var formato = tipo === 'm' ? /^[A-ZÑ&]{3}\d{6}[A-Z0-9]{3}$/ : /^[A-ZÑ&]{4}\d{6}[A-Z0-9]{3}$/;
    if (!formato.test(rfc)) {
      return {
        error: tipo === 'm'
          ? 'El RFC de persona moral lleva 3 letras, 6 números de la fecha (AAMMDD) y 3 caracteres de homoclave.'
          : 'El RFC de persona física lleva 4 letras, 6 números de la fecha (AAMMDD) y 3 caracteres de homoclave.',
        tipo: tipo
      };
    }
    var fecha = rfc.substr(tipo === 'm' ? 3 : 4, 6);
    if (!fechaValida(fecha)) return { error: 'La fecha del RFC (' + fecha + ', AAMMDD) no es válida.', tipo: tipo };
    var aviso = '';
    var generico = rfc === 'XAXX010101000' || rfc === 'XEXX010101000';
    if (!generico && rfcDigito(rfc) !== rfc.charAt(rfc.length - 1)) aviso = 'Revisa tu RFC: el último carácter no coincide.';
    return { error: '', aviso: aviso, tipo: tipo };
  }

  class Factura extends HTMLElement {
    connectedCallback() {
      if (this._td) return;
      this._td = true;
      var self = this;
      this.form = this.closest('form');
      this.check = this.querySelector('[data-td-fac-check]');
      this.fieldsBox = this.querySelector('[data-td-fac-fields]');
      this.status = this.querySelector('[data-td-fac-status]');
      this.f = {};
      ['rfc', 'razon', 'regimen', 'uso', 'cp', 'correo'].forEach(function (key) {
        self.f[key] = self.querySelector('[data-td-fac="' + key + '"]');
      });
      this.touched = {};

      // Si el tema volvió a pintar el cajón antes de guardar, se recupera lo último que se escribió.
      if (facMemoria) this.restore(facMemoria);

      this.addEventListener('input', function (event) {
        if (event.target === self.f.rfc) {
          var pos = self.f.rfc.selectionStart;
          var antes = self.f.rfc.value;
          var limpio = rfcLimpio(antes);
          if (limpio !== antes) {
            self.f.rfc.value = limpio;
            try { self.f.rfc.setSelectionRange(Math.max(0, pos - (antes.length - limpio.length)), Math.max(0, pos - (antes.length - limpio.length))); } catch (e) { /* nada */ }
          }
        }
        if (event.target === self.f.cp) self.f.cp.value = self.f.cp.value.replace(/\D/g, '').slice(0, 5);
        self.refresh(false);
        self.queueSave();
      });
      this.addEventListener('change', function (event) {
        if (event.target === self.check) {
          self.fieldsBox.hidden = !self.check.checked;
          if (self.check.checked && self.f.rfc && !self.f.rfc.value) self.f.rfc.focus();
          // En el cajón (celular) el tema calcula alturas al abrir; se le avisa que cambió el tamaño.
          if (self.getAttribute('data-contexto') === 'cajon') window.dispatchEvent(new Event('resize'));
        }
        self.refresh(false);
        self.queueSave(true);
      });
      // Enter en un campo de factura no debe enviar el formulario del carrito.
      this.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' && event.target.matches('input')) {
          event.preventDefault();
          event.target.blur();
        }
      });
      this.addEventListener('focusout', function (event) {
        var key = event.target.getAttribute && event.target.getAttribute('data-td-fac');
        if (key) {
          self.touched[key] = true;
          self.refresh(false);
        }
      });

      if (this.form && !this.form._tdFactura) {
        this.form._tdFactura = true;
        var form = this.form;
        form.addEventListener('submit', function (event) {
          var fac = form.querySelector('td-factura');
          if (fac && fac.isBlocking()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            fac.refresh(true);
          }
        }, true);
      }

      this.refresh(false);
    }

    disconnectedCallback() {
      facMemoria = this.snapshot();
    }

    snapshot() {
      var data = { on: this.check.checked };
      var f = this.f;
      Object.keys(f).forEach(function (key) { data[key] = f[key] ? f[key].value : ''; });
      return data;
    }

    restore(data) {
      var f = this.f;
      this.check.checked = !!data.on;
      this.fieldsBox.hidden = !data.on;
      Object.keys(f).forEach(function (key) { if (f[key] && data[key] !== undefined) f[key].value = data[key]; });
    }

    /** Valida todo, filtra los catálogos según el RFC y el régimen, y bloquea o libera el botón de pagar. */
    refresh(showAll) {
      var f = this.f;
      var on = this.check.checked;
      var errores = {};
      var avisos = {};

      var rfc = rfcLimpio(f.rfc.value);
      var r = revisarRfc(rfc);
      if (r.error) errores.rfc = r.error;
      if (r.aviso) avisos.rfc = r.aviso;

      // Régimen: solo los que corresponden a persona moral (12) o física (13).
      var tipo = r.tipo || (rfc.length === 12 ? 'm' : rfc.length === 13 ? 'f' : '');
      Array.prototype.forEach.call(f.regimen.options, function (option) {
        var t = option.getAttribute('data-tipo');
        var ok = !t || !tipo || t.indexOf(tipo) !== -1;
        option.disabled = !ok;
        option.hidden = !ok;
      });
      var regOpt = f.regimen.options[f.regimen.selectedIndex];
      if (regOpt && regOpt.disabled) {
        f.regimen.value = '';
        errores.regimen = tipo === 'm' ? 'Ese régimen es de persona física; elige uno de persona moral.' : 'Ese régimen es de persona moral; elige uno de persona física.';
      } else if (!f.regimen.value) {
        errores.regimen = 'Elige tu régimen fiscal.';
      }

      // Uso de CFDI: con régimen 605, 616 y otros de persona física sin actividad, el SAT solo permite S01 de esta lista.
      var general = !f.regimen.value || USO_GENERAL.indexOf(f.regimen.value) !== -1;
      Array.prototype.forEach.call(f.uso.options, function (option) {
        var ok = general || option.value === 'S01';
        option.disabled = !ok;
        option.hidden = !ok;
      });
      if (!general && f.uso.value !== 'S01') {
        f.uso.value = 'S01';
        avisos.uso = 'Con el régimen ' + f.regimen.value + ' el uso de CFDI disponible es S01 (Sin efectos fiscales).';
      }
      if (!f.uso.value) errores.uso = 'Elige el uso de CFDI.';

      if (f.razon.value.trim().length < 2) errores.razon = 'Escribe tu razón social o nombre, como aparece en tu Constancia de Situación Fiscal.';
      if (!/^\d{5}$/.test(f.cp.value)) errores.cp = 'El código postal fiscal debe tener 5 dígitos.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.correo.value.trim())) errores.correo = 'Escribe un correo válido para enviarte la factura.';

      var self = this;
      Object.keys(f).forEach(function (key) {
        var msg = self.querySelector('[data-td-fac-msg="' + key + '"]');
        if (!msg) return;
        var mostrar = on && (showAll || self.touched[key] || (key === 'rfc' && rfc.length >= 12));
        var texto = mostrar && errores[key] ? errores[key] : (on && avisos[key] ? avisos[key] : '');
        msg.textContent = texto;
        msg.className = 'td-fac__msg' + (texto ? (mostrar && errores[key] ? ' is-error' : ' is-warning') : '');
        if (f[key]) f[key].setAttribute('aria-invalid', mostrar && errores[key] ? 'true' : 'false');
      });

      this.errores = on ? errores : {};
      this.syncHidden();
      this.lockCheckout();
    }

    isBlocking() {
      return this.check.checked && Object.keys(this.errores || {}).length > 0;
    }

    values() {
      var f = this.f;
      if (!this.check.checked) {
        return { 'Factura': '', 'RFC': '', 'Razón social': '', 'Régimen fiscal': '', 'Uso CFDI': '', 'CP fiscal': '', 'Correo factura': '' };
      }
      var reg = f.regimen.options[f.regimen.selectedIndex];
      var uso = f.uso.options[f.uso.selectedIndex];
      return {
        'Factura': 'Sí',
        'RFC': rfcLimpio(f.rfc.value),
        'Razón social': f.razon.value.trim(),
        'Régimen fiscal': f.regimen.value && reg ? reg.textContent.trim() : '',
        'Uso CFDI': uso ? uso.textContent.trim() : '',
        'CP fiscal': f.cp.value.trim(),
        'Correo factura': f.correo.value.trim()
      };
    }

    syncHidden() {
      var values = this.values();
      var self = this;
      FAC_KEYS.forEach(function (key) {
        var input = self.querySelector('[data-td-fac-attr="' + key + '"]');
        if (input) input.value = values[key];
      });
    }

    lockCheckout() {
      var block = this.isBlocking();
      var root = this.form || this.parentNode;
      var buttons = root ? root.querySelectorAll('[name="checkout"], a[href*="/checkout"]') : [];
      Array.prototype.forEach.call(buttons, function (button) {
        button.toggleAttribute('disabled', block);
        button.setAttribute('aria-disabled', block ? 'true' : 'false');
        button.classList.toggle('button--disabled', block);
        button.classList.toggle('td-fac-bloqueado', block);
      });
      if (root) root.classList.toggle('td-fac-incompleta', block);
      if (block) {
        var faltan = [];
        var nombres = { rfc: 'RFC', razon: 'razón social', regimen: 'régimen fiscal', uso: 'uso de CFDI', cp: 'código postal fiscal', correo: 'correo' };
        Object.keys(this.errores).forEach(function (key) { faltan.push(nombres[key]); });
        this.status.textContent = 'Para pagar, completa o corrige los datos de factura (' + faltan.join(', ') + ') o desmarca «Necesito factura».';
        this.status.hidden = false;
      } else {
        this.status.textContent = '';
        this.status.hidden = true;
      }
    }

    /** Guarda en el carrito (con una pausa para no mandar una petición por cada tecla). */
    queueSave(now) {
      var self = this;
      facMemoria = this.snapshot();
      clearTimeout(this._saveTimer);
      this._saveTimer = setTimeout(function () { self.save(); }, now ? 0 : 600);
    }

    save() {
      // Un guardado a la vez: si hay uno en camino, al terminar se guarda otra vez con los datos más recientes
      // (dos peticiones al mismo tiempo pueden llegar en desorden y dejar datos viejos en el carrito).
      if (Factura.inflight) {
        Factura.again = this;
        return Factura.inflight;
      }
      var self = this;
      var attributes = this.values();
      var key = JSON.stringify(attributes);
      if (key === Factura.lastSaved) return Promise.resolve();
      Factura.inflight = fetch(((window.routes && window.routes.cartUrl) || '/cart') + '/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      }).then(function (response) {
        if (response.ok) {
          Factura.lastSaved = key;
          facMemoria = null;
        }
      }).catch(function () { /* se vuelve a intentar con el siguiente cambio y al pagar viajan en el formulario */ }).then(function () {
        Factura.inflight = null;
        var next = Factura.again;
        Factura.again = null;
        if (next) return (next.isConnected ? next : self).save();
      });
      return Factura.inflight;
    }
  }
  Factura.lastSaved = null;
  Factura.inflight = null;
  Factura.again = null;

  if (!window.customElements.get('td-pedido-rapido')) window.customElements.define('td-pedido-rapido', PedidoRapido);
  if (!window.customElements.get('td-agregar-rapido')) window.customElements.define('td-agregar-rapido', AgregarRapido);
  if (!window.customElements.get('td-cuadricula')) window.customElements.define('td-cuadricula', Cuadricula);
  if (!window.customElements.get('td-factura')) window.customElements.define('td-factura', Factura);
})();
